// src/pages/Pass.tsx
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Award, Check, Download, LogOut, ShieldCheck, Ticket } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Celebrate } from '../components/Celebrate'
import { ImportantNote } from '../components/ImportantNote'
//import { Bunting } from '../components/AuthLayout'
import { flag } from '../components/flags'
import { Card, CardContent, CardEyebrow, CardHeader, CardTitle, Note } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { cn } from '../lib/cn'
import { download, get, post } from '../lib/api'
import { clearSession, getSession, saveSession } from '../lib/session'
import { ClaimCertificate } from './ClaimCertificate'
import { useLivePass } from '../lib/useLivePass'

interface BoothProgress {
  boothNo: number
  school: string
  attended: boolean
  attendedAt: string | null
}

interface PassData {
  regNo: string | null
  /** Null for an attendee from another institution. */
  studentId: string | null
  name: string
  school: string
  boothsVisited: number
  boothsRemaining: number
  certificateThreshold: number
  totalBooths: number
  drawEligible: boolean
  luckyDrawThreshold: number
  completed: boolean
  certificateNo: string | null
  certificateVerifyUrl: string | null
  certificateIssuedAt: string | null
  certificateEmailed: boolean
  /** The claim form has been filled in; the download is unlocked. */
  certificateClaimed: boolean
  booths: BoothProgress[]
}

const STRIP_FLAGS = [

  'my', 'in', 'cn', 'id', 'bd', 'pk', 'ng', 'sa', 'jp', 'gb', 'ae', 'tw',
  'qa', 'ps', 'om', 'bh', 'kw', 'jo', 'lb', 'ir', 'iq', 'ph', 'sg', 'lk', 'np'

];

const cardShell =
  'w-full overflow-hidden rounded-3xl border border-white bg-white text-slate-900 shadow-xl shadow-blue-900/10'
const primaryBtn =
  'h-11 rounded-xl bg-[#1B2A6B] py-0 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#141F55] disabled:opacity-60'
const outlineBtn =
  'h-11 rounded-xl border border-slate-200 bg-white py-0 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60'

/** Same page frame as Login / Register / Verify, but single column for the ticket. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh select-none flex-col bg-gradient-to-br from-[#BFD8FA] via-[#E9F1FD] to-[#F5EFFC] font-sans text-[#0B1E3F] [-webkit-tap-highlight-color:transparent]">

      <main className="mx-auto w-full max-w-md flex-1 px-4 pb-4 pt-6">{children}</main>
      <footer className="px-3 py-3 text-center text-[11px] text-slate-500">
        <p className="font-semibold text-slate-700">Lincoln International SDG &amp; Cultural Day 2026</p>
        Technology Partner: <b className="text-slate-700">Techvision Softwares Sdn. Bhd.</b>
        <span className="hidden sm:inline"> · www.techvisionsoft.com</span>
      </footer>
    </div>
  )
}

/** Circular progress: visited / total. */
function ProgressRing({ value, total }: { value: number; total: number }) {
  const r = 28
  const c = 2 * Math.PI * r
  const pct = total > 0 ? value / total : 0
  return (
    <div className="relative size-[64px] shrink-0">
      <svg viewBox="0 0 80 80" className="size-full -rotate-90" aria-hidden>
        <circle cx="40" cy="40" r={r} fill="none" stroke="#F1F5F9" strokeWidth="6" />
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          stroke="#059669"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          className="transition-[stroke-dashoffset] duration-700"
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div className="leading-none">
          <span className="text-base font-bold tabular-nums text-[#0B1E3F]">{value}</span>
          <span className="text-[11px] font-medium text-slate-400">/{total}</span>
          <p className="mt-0.5 text-[5px] text-6xl font-semibold uppercase tracking-wider text-black">Booths</p>
        </div>
      </div>
    </div>
  )
}

export default function Pass() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [exchanging, setExchanging] = useState(false)

  /** Which booth circle was tapped, if any. */
  const [booth, setBooth] = useState<BoothProgress | null>(null)
  const [confirmingSignOut, setConfirmingSignOut] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  /** The collect-your-certificate form, opened from the card below. */
  const [claiming, setClaiming] = useState(false)

  const passToken = params.get('k')

  const arrival = location.state as { celebrate?: boolean; returning?: boolean } | null
  const [celebrating, setCelebrating] = useState(() => !!arrival?.celebrate)
  /*
   * The two rules, shown the moment the celebration closes.
   *
   * Chained rather than merged into one card: the celebration is confetti and
   * a registration number, and a warning sitting under that gets read as part
   * of the good news and dismissed with it. A returning student has seen it
   * already, so only a first registration gets it.
   */
  const [noting, setNoting] = useState(false)
  const [isReturning] = useState(() => !!arrival?.returning)

  useEffect(() => {
    if (arrival?.celebrate) navigate(location.pathname, { replace: true, state: null })
    // Runs once: by design this clears the very state it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // An e-mailed pass link carries ?k=<passToken>; trade it for a 1-day session.
  useEffect(() => {
    if (!passToken || exchanging) return

    setExchanging(true)
    post<{ sessionToken: string; expiresAt: string }>('/api/student/pass/open', { passToken })
      .then((res) => {
        saveSession(res.sessionToken, res.expiresAt)
        // Drop the token from the URL so it is not left in history or shared.
        setParams({}, { replace: true })
        queryClient.invalidateQueries({ queryKey: ['pass-qr'] })
      })
      .catch(() => navigate('/', { replace: true }))
      .finally(() => setExchanging(false))
  }, [passToken, exchanging, navigate, setParams, queryClient])

  const hasSession = !!getSession()

  // Pushed, not polled: the server sends the pass on connect and whenever a booth scans this student.
  const live = useLivePass<PassData>(hasSession && !passToken)
  const data = live.data
  const isLoading = !data && live.state !== 'error'
  const isError = !data && live.state === 'error'

  // The QR is fetched once and kept: it is identical for the life of the pass.
  const qr = useQuery({
    queryKey: ['pass-qr'],
    queryFn: () => get<{ qrDataUrl: string }>('/api/student/pass/qr'),
    enabled: hasSession && !passToken,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    // Signing out does its own redirect; this guard would race it.
    if (!hasSession && !passToken && !signingOut) navigate('/', { replace: true })
  }, [hasSession, passToken, signingOut, navigate])

  async function downloadPdf() {
    if (downloading) return
    setDownloading(true)
    setDownloadError(null)
    try {
      await download('/api/student/certificate.pdf', `Certificate_${data?.studentId ?? data?.regNo ?? 'SDG2026'}.pdf`)
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'please try again')
    } finally {
      setDownloading(false)
    }
  }

  async function signOut() {
    if (signingOut) return
    setSigningOut(true)

    // Revoke server-side first; the session is useless locally either way.
    await post('/api/student/logout', {}).catch(() => { })

    // Closing the dialog and leaving happen in one commit (rAF never fires in a background tab).
    setConfirmingSignOut(false)
    clearSession()
    navigate('/', { replace: true })

    // Cache is dropped only after leaving, so the page never flashes an empty state.
    queryClient.removeQueries({ queryKey: ['pass-qr'] })
  }

  /* ---------- Loading ---------- */
  if (passToken || exchanging || isLoading) {
    return (
      <Shell>
        <Card className={cardShell}>
          <CardContent className="grid place-items-center gap-3 py-16">
            <span className="size-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-orange-500" />
            <p className="text-sm font-medium text-slate-500">Opening your pass…</p>
          </CardContent>
        </Card>
      </Shell>
    )
  }

  /* ---------- Session ended ---------- */
  if (isError || !data) {
    return (
      <Shell>
        <Card className={cardShell}>
          <CardHeader className="space-y-1 px-6 pb-2 pt-6">
            <CardEyebrow className="text-[11px] font-bold uppercase tracking-widest text-orange-600">
              Digital pass
            </CardEyebrow>
            <CardTitle className="text-2xl font-bold text-[#0B1E3F]">Your session has ended</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 pt-3">
            <Note className="rounded-xl border-blue-100 bg-blue-50/80 text-sm text-slate-600">
              Open the pass link in your e-mail again, or register if you have not yet done so.
            </Note>
            <Button
              block
              className="mt-4 h-11 rounded-xl bg-orange-500 py-0 text-sm font-semibold text-white hover:bg-orange-600"
              onClick={() => {
                clearSession()
                navigate('/', { replace: true })
              }}
            >
              Back to registration
            </Button>
          </CardContent>
        </Card>
      </Shell>
    )
  }

  const toGo = data.luckyDrawThreshold - data.boothsVisited

  return (
    <Shell>
      <Celebrate
        open={celebrating}
        onOpenChange={(open) => {
          setCelebrating(open)
          /* Hand straight over, so there is no pass visible in between. */
          if (!open && !isReturning) setNoting(true)
        }}
        returning={isReturning}
        name={data.name}
        regNo={data.regNo}
      />

      <ImportantNote open={noting} onOpenChange={setNoting} />

      <Card className={cardShell}>
        {/* ---------- Stub ---------- */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#2B3E9A] via-[#1B2A6B] to-[#0F1A48] px-5 pb-6 pt-5 text-center">
          <div className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-white/5" />
          <div className="pointer-events-none absolute -bottom-14 -left-8 size-40 rounded-full bg-amber-400/10" />

          <p className="relative flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-200">
            <Ticket className="size-3.5 text-amber-400" />
            SDG &amp; Cultural Day 2026
          </p>

          {data.regNo && (
            <>
              <p className="relative mt-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/60">
                Registration No.
              </p>
              <p className="relative mt-1 text-[44px] font-black leading-none tracking-[0.14em] text-white tabular-nums">
                {data.regNo}
              </p>
            </>
          )}

          <div className="relative mt-4 flex items-center justify-center -space-x-1.5">
            {STRIP_FLAGS.map((code) => (
              <img
                key={code}
                src={flag(code, 40)}
                alt=""
                draggable={false}
                className="size-5 rounded-full object-cover ring-2 ring-[#1B2A6B]"
              />
            ))}
          </div>
        </div>


        <CardContent className="px-5 pb-6 pt-7 text-center sm:px-7">
          {/* ---------- Holder ---------- */}
          <h1 className="text-2xl font-extrabold capitalize leading-tight text-[#0B1E3F]">{data.name}</h1>
          <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2">
            {/*
              Their Lincoln Student ID, or — for a visitor from another
              institution, who has none — their registration code, which is
              what a booth asks for anyway.
            */}
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold tracking-wide text-slate-700 tabular-nums">
              {data.studentId ?? data.regNo}
            </span>
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              {data.school}
            </span>
          </div>

          {/* ---------- QR ---------- */}
          <div className="relative mx-auto mt-6 w-fit">
            <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-amber-200/50 via-blue-200/40 to-transparent blur-xl" />
            <div className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-lg shadow-blue-900/5">
              {qr.data ? (
                <img
                  src={qr.data.qrDataUrl}
                  alt={`QR pass for ${data.name}`}
                  draggable={false}
                  className="block size-[200px] sm:size-[220px]"
                />
              ) : (
                <div className="size-[200px] animate-pulse rounded-lg bg-slate-100 sm:size-[220px]" />
              )}
              <span className="pointer-events-none absolute left-2 top-2 size-6 rounded-tl-lg border-l-[3px] border-t-[3px] border-orange-500" />
              <span className="pointer-events-none absolute right-2 top-2 size-6 rounded-tr-lg border-r-[3px] border-t-[3px] border-orange-500" />
              <span className="pointer-events-none absolute bottom-2 left-2 size-6 rounded-bl-lg border-b-[3px] border-l-[3px] border-orange-500" />
              <span className="pointer-events-none absolute bottom-2 right-2 size-6 rounded-br-lg border-b-[3px] border-r-[3px] border-orange-500" />
            </div>
          </div>



          {/* ---------- Progress ---------- */}
          <div className="mt-4 flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-4 text-left shadow-xs">
            <ProgressRing value={data.boothsVisited} total={data.totalBooths} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#0B1E3F]">
                {data.boothsRemaining === 0
                  ? 'Certificate earned'
                  : `${data.boothsRemaining} booth${data.boothsRemaining === 1 ? '' : 's'} to your certificate`}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                Visit {data.luckyDrawThreshold} to enter the lucky draw,{' '}
                {data.certificateThreshold} for your certificate.
              </p>
              {data.drawEligible ? (
                <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                  <Check className="size-3.5" strokeWidth={2.5} /> In lucky draw
                </span>
              ) : (
                <p className="mt-2 text-xs font-medium text-slate-600">
                  <span className="font-semibold text-[#0B1E3F]">{toGo} more</span> to enter the lucky draw
                </p>
              )}
            </div>
          </div>

          {/* ---------- Booths ---------- */}
          <div className="mt-6 text-left">
            <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Your booths
            </p>
            <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
              {data.booths.map((b) => (
                <button
                  key={b.boothNo}
                  type="button"
                  onClick={() => setBooth(b)}
                  aria-label={`Booth ${b.boothNo}, ${b.school}, ${b.attended ? 'visited' : 'not visited yet'}`}
                  className={cn(
                    'relative grid aspect-square w-full place-items-center rounded-full border-2 text-xs font-bold transition-transform sm:text-sm',
                    'active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
                    b.attended
                      ? 'border-emerald-500 bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-sm shadow-emerald-500/30'
                      : 'border-slate-200 bg-white text-slate-400 hover:border-orange-400 hover:text-orange-500',
                  )}
                >
                  {b.boothNo}

                </button>
              ))}
            </div>
            <p className="mt-3 text-center text-[11.5px] text-slate-500">
              Tap a number to see the booth. Visit them in any order.
            </p>
          </div>

          {/* ---------- Certificate ---------- */}
          {data.completed && (
            <div className="mt-6 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white text-left">
              <div className="flex items-start gap-3 px-4 pt-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-emerald-500 text-white shadow shadow-emerald-500/30">
                  <Award className="size-5" strokeWidth={2.2} />
                </span>
                <div className="min-w-0">
                  <p className="text-[15px] font-bold leading-tight text-[#0B1E3F]">
                    Certificate of Participation
                  </p>
                  <p className="mt-1 text-xs leading-snug text-slate-500">
                    {data.boothsVisited} of {data.totalBooths} booths visited
                    {data.certificateClaimed && data.certificateEmailed
                      ? " — e-mailed to you"
                      : ""}
                    .
                  </p>
                </div>
              </div>

              {!data.certificateClaimed ? (
                /*
                  Earned but not collected. The Committee made the certificate
                  something a student claims rather than something that lands
                  in their inbox, so this is the only route to it — and it is
                  stated plainly rather than dressed up as a download that
                  then asks for something.
                */
                <div className="p-4 pt-3">
                  <p className="text-xs leading-snug text-slate-500">
                    Your certificate is ready. Tell us how the day went and send a photo, and it
                    downloads straight away.
                  </p>
                  <Button
                    block
                    className={cn(primaryBtn, "mt-3")}
                    onClick={() => setClaiming(true)}
                  >
                    <Award className="size-4" strokeWidth={2.2} />
                    Collect my certificate
                  </Button>
                </div>
              ) : data.certificateNo ? (
                <>
                  <div className="mx-4 mt-3 rounded-xl border border-emerald-200 bg-white px-3 py-2">
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Certificate No.
                    </p>
                    <p className="select-text font-mono text-[15px] font-bold tabular-nums text-[#0B1E3F]">
                      {data.certificateNo}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 p-4 sm:flex-row">
                    <Button block disabled={downloading} onClick={downloadPdf} className={primaryBtn}>
                      <Download className="size-4" strokeWidth={2.2} />
                      {downloading ? 'Preparing…' : 'Download certificate'}
                    </Button>

                    {data.certificateVerifyUrl && (
                      <Button block asChild className={outlineBtn}>
                        <a href={data.certificateVerifyUrl}>
                          <ShieldCheck className="size-4" strokeWidth={2.2} />
                          Verify
                        </a>
                      </Button>
                    )}
                  </div>

                  {downloadError && (
                    <p className="px-4 pb-4 text-xs text-orange-600">
                      Could not open the certificate — {downloadError}. It is also attached to your
                      e-mail.
                    </p>
                  )}
                </>
              ) : (
                <p className="px-4 pb-4 pt-3 text-xs text-slate-500">
                  Your certificate is being prepared — it will appear here in a moment.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-3 text-center">
        <Button
          variant="link"
          className="h-auto py-2 text-[13px] font-medium text-slate-500 hover:text-slate-800"
          onClick={() => setConfirmingSignOut(true)}
        >
          <LogOut className="size-3.5" strokeWidth={2.2} />
          Sign out
        </Button>
      </div>

      {/*
        Collect the certificate.

        No refetch on success: issuing publishes on `student:${id}`, and the
        pass stream re-sends the whole pass on any event for this student. The
        download button appears by itself a moment later.
      */}
      {claiming && (
        <ClaimCertificate
          onClose={() => setClaiming(false)}
          onClaimed={() => setClaiming(false)}
        />
      )}

      {/* ---------- Booth dialog ---------- */}
      <Dialog open={!!booth} onOpenChange={(open) => !open && setBooth(null)}>
        <DialogContent className="rounded-3xl p-0">
          {booth && (
            <div className="flex flex-col items-center px-6 pb-6 pt-7 text-center">
              <span
                className={cn(
                  'mb-3 grid size-16 place-items-center rounded-full border-2 text-2xl font-extrabold',
                  booth.attended
                    ? 'border-emerald-500 bg-gradient-to-br from-emerald-400 to-emerald-600 text-white'
                    : 'border-slate-200 bg-slate-50 text-slate-400',
                )}
              >
                {booth.boothNo}
              </span>

              <DialogTitle className="text-lg font-bold text-[#0B1E3F]">Booth {booth.boothNo}</DialogTitle>
              <DialogDescription className="mt-1 text-sm font-semibold text-blue-700">
                {booth.school}
              </DialogDescription>

              {booth.attended && booth.attendedAt ? (
                <Note
                  tone="green"
                  className="mt-4 inline-flex items-center gap-2 rounded-xl text-sm"
                >
                  <Check className="size-4" strokeWidth={3} />
                  Visited at{' '}
                  {new Date(booth.attendedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Note>
              ) : (
                <Note className="mt-4 rounded-xl border-blue-100 bg-blue-50/80 text-sm text-slate-600">
                  Not visited yet — show your QR code at this booth to be marked present.
                </Note>
              )}

              <Button
                block
                className="mt-5 h-11 rounded-xl bg-orange-500 py-0 text-sm font-semibold text-white hover:bg-orange-600"
                onClick={() => setBooth(null)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Sign-out dialog ---------- */}
      <Dialog open={confirmingSignOut} onOpenChange={setConfirmingSignOut}>
        <DialogContent className="rounded-3xl p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7 text-center">
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-orange-100 text-orange-600">
              <LogOut className="size-5" strokeWidth={2.2} />
            </span>

            <DialogTitle className="text-lg font-bold text-[#0B1E3F]">Sign out of your pass?</DialogTitle>
            <DialogDescription className="mt-1.5 text-sm text-slate-500">
              Your booth progress is kept. You can reopen this pass any time from the link in your
              e-mail.
            </DialogDescription>

            <div className="mt-5 flex gap-2.5">
              <Button
                block
                disabled={signingOut}
                onClick={() => setConfirmingSignOut(false)}
                autoFocus
                className={outlineBtn}
              >
                Stay signed in
              </Button>
              <Button block disabled={signingOut} onClick={signOut} className={primaryBtn}>
                {signingOut ? 'Signing out…' : 'Sign out'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  )
}
